import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import { booleanFormToUpdateConverter, currencyInput } from '@/_schemas/generic';

import {
  useCommissionPeriodsBulkCreateMutation,
  useCommissionPeriodsBulkDeleteMutation,
  useCommissionPeriodsBulkUpdateMutation,
} from '../apis';
import type { TeamMemberFormSchema } from '../schemas';
import type { CommissionPeriodsFormSchema, CommissionPeriodsQuerySchema } from '../schemas/commission-periods-schema';

export function useCommissionPeriods(
  form: UseFormReturn<TeamMemberFormSchema>,
  oldCommissions: CommissionPeriodsQuerySchema[]
) {
  const deleteMutations = useCommissionPeriodsBulkDeleteMutation();
  const updateMutations = useCommissionPeriodsBulkUpdateMutation();
  const createMutations = useCommissionPeriodsBulkCreateMutation();

  const manageCommissionPeriods = useCallback(async () => {
    if (!form?.formState?.dirtyFields?.commission_periods) {
      return;
    }
    const commissions = form.getValues('commission_periods') || [];
    const id = form.getValues('id');

    const created = commissions.filter((t) => t.id < 0);

    const deleted = oldCommissions.filter((ot) => !commissions.find((t) => t.id === ot.id));

    const updated = commissions.filter((t) => t.id > 0);

    if (deleted && deleted.length > 0) {
      await deleteMutations.mutateAsync({
        items: deleted.map((t) => t.id!),
      });
    }

    if (updated.length > 0) {
      await updateMutations.mutateAsync({
        items: updated.map((c) => ({
          ...c,
          team_member_id: id,
          commission_plan_id: c.commission_plan?.id ?? null,

          bonus_amount_paid: currencyInput.formToUpdateConverter(c.bonus_amount_paid),
          bonus_paid: booleanFormToUpdateConverter(c.bonus_paid),
        })),
      });
    }

    let newlyCreated: CommissionPeriodsFormSchema[] = [];
    if (created.length > 0) {
      const res = await createMutations.mutateAsync({
        items: created.map((c) => ({
          ...c,
          team_member_id: id,
          commission_plan_id: c.commission_plan?.id ?? null,

          bonus_amount_paid: currencyInput.formToUpdateConverter(c.bonus_amount_paid),
          bonus_paid: booleanFormToUpdateConverter(c.bonus_paid),
        })),
      });

      newlyCreated = res.map((r, idx) => {
        const original = created[idx] ?? ({} as CommissionPeriodsFormSchema);
        return {
          ...original,
          id: r.id,
        } as CommissionPeriodsFormSchema;
      });
    }

    const latestTiers = [...updated, ...newlyCreated];
    form.setValue('commission_periods', latestTiers, { shouldDirty: false });
    return latestTiers;
  }, [form, deleteMutations, createMutations, updateMutations, oldCommissions]);

  return {
    manageCommissionPeriods,
    isManageCommissionPeriodsPending:
      deleteMutations.isPending || updateMutations.isPending || createMutations.isPending,
  };
}
